import Button from 'components/Button';
import Overlay from 'components/Layout/Overlay';
import useKeyListener from 'hooks/useKeyListener';
import useOutsideDetector from 'hooks/useOutsideDetector';
import {FC, ReactNode, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {t} from 'ttag';

import FilterByMenuContainer from './FilterByMenuContainer';
import useFilterNames from './hooks/useFilterNames';

export type FilterByMenuProps = {
    ariaLabel?: string;
    children?: ReactNode;
    className?: string;
    disabled?: boolean;
    layoutOffset?: number;
    title?: string;
};

const FilterByMenu: FC<FilterByMenuProps> = ({
    children,
    className,
    disabled,
    layoutOffset,
    title = t`Filter By`,
}) => {
    const [show, setShow] = useState(() => false);

    const filterNames = useFilterNames(children);

    const {watch} = useFormContext();
    const formValues = watch(filterNames);
    const numberOfActiveFilters = Object.values(formValues).reduce(
        (counter, value) => (value ? counter + 1 : counter),
        0,
    );

    const refFilterButton = useRef<HTMLButtonElement>(null);
    const refPopUp = useRef<HTMLDivElement>(null);

    useOutsideDetector([refFilterButton, refPopUp], () => {
        setShow(false);
    });
    useKeyListener(['Escape', 'Tab'], () => setShow(false));

    return (
        <>
            <Button
                aria-expanded={show}
                className={twMerge('h-8', className)}
                data-disabled={disabled}
                icon="filter"
                kind="secondary"
                onClick={() => !disabled && setShow((prevState) => !prevState)}
                onKeyDown={(event) =>
                    !disabled &&
                    event.key === 'Enter' &&
                    setShow((prevState) => !prevState)
                }
                ref={refFilterButton}
            >
                {t`Filter By`}
                {numberOfActiveFilters > 0 && (
                    <>
                        <span className="mx-2">|</span>
                        <span className="font-bold text-green-500">
                            {numberOfActiveFilters}
                        </span>
                    </>
                )}
            </Button>
            {show && refFilterButton.current && (
                <Overlay
                    element={refFilterButton.current}
                    layoutLayer="z-dropdown"
                    layoutMethod="bottom"
                    layoutOffset={layoutOffset}
                >
                    <FilterByMenuContainer
                        ref={refPopUp}
                        resettableFilters={filterNames}
                        title={title}
                    >
                        {children}
                    </FilterByMenuContainer>
                </Overlay>
            )}
        </>
    );
};

export default FilterByMenu;
